import { ModalSidebarItemStatus } from "eone-ui/modal/modal-constants";

// Small invariants shared by every connect flow (install / reconnect / child / user-connect).
// They live here once so the rule can't drift between the per-flow controllers.

/**
 * Which action the primary button triggers: save authenticated settings once authenticated,
 * otherwise authorize (OAuth) or connect (non-redirect).
 * @param {{ phase: string, handleSaveSettings: Function, handleAuthorize: Function, handleConnect: Function }} connect
 * @param {{ HasRedirectUri?: boolean } | null | undefined} connectorAuth
 * @returns {Function}
 */
function deriveSubmitAction(connect, connectorAuth) {
    if (connect.phase === "authenticated") return connect.handleSaveSettings;
    return connectorAuth?.HasRedirectUri ? connect.handleAuthorize : connect.handleConnect;
}

/**
 * Sidebar completion status for the Connect (page 1) and Settings (page 2) steps, from the phase.
 * @param {string} phase
 * @returns {{ page1Status: string, page2Status: string }}
 */
function derivePageStatuses(phase) {
    return {
        page1Status: phase !== "initial" ? ModalSidebarItemStatus.Complete : ModalSidebarItemStatus.Incomplete,
        page2Status: phase === "connected" ? ModalSidebarItemStatus.Complete : ModalSidebarItemStatus.Incomplete,
    };
}

/**
 * The install-flow page model: the ordered Connect [+ Settings] [+ Lists] pages with their
 * sidebar statuses and a navigation gate. `entityMode` (the post-connect list-selection step)
 * is a terminal, dominant state: when true the model is fully determined and ignores `phase`
 * (connect + settings show Complete, Lists is the active step, and navigation is locked to Lists).
 * Pre-entity behavior mirrors the original derivePageStatuses + setActivePageGated rules.
 *
 * @param {{ phase: string, hasSettingsStage: boolean, entityMode: boolean }} input
 * @returns {{ pages: { value: string, status: number }[], canActivate: (value: string) => boolean }}
 */
function deriveInstallPageModel({ phase, hasSettingsStage, entityMode }) {
    if (entityMode) {
        const pages = [{ value: "connect", status: ModalSidebarItemStatus.Complete }];
        if (hasSettingsStage) pages.push({ value: "settings", status: ModalSidebarItemStatus.Complete });
        pages.push({ value: "lists", status: ModalSidebarItemStatus.Incomplete });
        return { pages, canActivate: (value) => value === "lists" };
    }
    const { page1Status, page2Status } = derivePageStatuses(phase);
    const pages = [{ value: "connect", status: page1Status }];
    if (hasSettingsStage) pages.push({ value: "settings", status: page2Status });
    return {
        pages,
        canActivate: (value) => {
            if (!pages.some(p => p.value === value)) return false;
            return value === "settings" ? phase !== "initial" : true;
        },
    };
}

/**
 * Whether authenticated settings may be shown / are shown inline, derived from the connect phase.
 * @param {{ phase: string, isSubmitting: boolean }} connect
 * @returns {{ canUseAuthenticatedSettings: boolean, shouldShowAuthenticatedSettings: boolean }}
 */
function deriveAuthenticatedSettingsVisibility(connect) {
    const canUseAuthenticatedSettings = connect.phase === "authenticated" || connect.phase === "connected";
    return { canUseAuthenticatedSettings, shouldShowAuthenticatedSettings: canUseAuthenticatedSettings || connect.isSubmitting };
}

/**
 * Whether a connect/authorize/save-settings/validate request is in flight. While busy, connect
 * surfaces lock their inputs and flow controls — edits made mid-request are ignored (values were
 * captured at click time) and only flip hasUnsavedChanges into a confusing state.
 * @param {{ isSubmitting: boolean, isValidating: boolean }} connect
 * @returns {boolean}
 */
function isConnectBusy(connect) {
    return !!(connect.isSubmitting || connect.isValidating);
}

/**
 * Full-page navigate to an authorize redirect URI if present. Returns whether it redirected.
 * @param {{ redirectUri?: string } | null | undefined} authorizeResult
 * @returns {boolean}
 */
function applyAuthorizeRedirect(authorizeResult) {
    if (authorizeResult?.redirectUri) { window.location.href = authorizeResult.redirectUri; return true; }
    return false;
}

/**
 * "No choice → skip the modal." There is exactly one auth, nothing for the user to fill or pick,
 * and we are not already coming back from a provider round-trip (which would loop). Returns the
 * action the caller should take instead of showing a modal whose only control is "Connect":
 *
 * - `"authorize"` — the auth has a redirect URI; send the user to the provider.
 * - `"connect"`   — the auth collects no credentials and has no authenticated settings; just create
 *                   the connector.
 * - `null`        — show the modal.
 *
 * Flows differ in WHICH inputs they feed, so the rule lives here once and the policy is passed in:
 * reconnect requires there be no authenticated settings to pick (`requireNoAuthSettings`); only
 * install opts into the credential-less connect (`allowUnattendedConnect`). UI state can also block
 * both actions unconditionally (`hasSidecarChoice`) regardless of flow.
 *
 * @param {{
 *   authCount: number,
 *   hasRedirectUri?: boolean,
 *   hasUserSettings?: boolean,
 *   hasAuthSettings?: boolean,
 *   isAuthReturn?: boolean,
 *   hasSidecarChoice?: boolean,
 *   requireNoAuthSettings?: boolean,
 *   allowUnattendedConnect?: boolean,
 * }} input
 * @returns {"authorize" | "connect" | null}
 */
function deriveUnattendedAction(input) {
    if (input.authCount !== 1) return null;
    if (input.hasUserSettings) return null;
    if (input.isAuthReturn) return null;
    if (input.hasSidecarChoice) return null;
    if (input.hasRedirectUri) {
        if (input.requireNoAuthSettings && input.hasAuthSettings) return null;
        return "authorize";
    }
    if (!input.allowUnattendedConnect) return null;
    // The frontend can't see AuthenticationType — HasRedirectUri is true only for
    // OAuth2AuthorizationCode (AuthenticationHelper.cs:71) — so "no redirect + no user settings" is
    // NOT proof that no credentials are collected. A ConstantSettings entry with SaveAs = PerUser
    // yields credentials server-side from an empty request body (AuthenticationHelper.cs:101-107),
    // and for an auth shaped that way with authenticated settings, the create endpoint
    // (ConnectorAuthV2Controller.Connect) returns { Success: true, SessionToken } having created
    // nothing — it expects those settings to be saved next. So authenticated settings mean there IS
    // something to fill; show the modal rather than auto-connecting. (Contrast the authorize branch
    // above: OAuth always yields credentials via the round-trip, so its authenticated settings are
    // collected afterward — that's what requireNoAuthSettings guards there.)
    if (input.hasAuthSettings) return null;
    return "connect";
}

/**
 * The help-article slug for the currently selected auth, for the modal's help icon. Read from the
 * auth list (every flow's GetAuths returns HelpArticleSlug per auth; connect-user's one-element list
 * is the auth detail itself), so it stays resolved even while the auth detail is reloading on a switch.
 * Null when nothing is selected or the auth has no help article (the shell then renders no icon).
 * @param {{ ConnectorAuthId: string, HelpArticleSlug?: string }[]} auths
 * @param {string | null | undefined} selectedAuthId
 * @returns {string | null}
 */
function deriveHelpLink(auths, selectedAuthId) {
    return (auths ?? []).find(a => a.ConnectorAuthId === selectedAuthId)?.HelpArticleSlug ?? null;
}

/**
 * Decide whether the connect UI needs two pages (Connect + Settings) or one flat form.
 * Mirrors the original `hasPage1Content` / `hasAuthSettings` / `usePages` derivation.
 *
 * @param {{ UserSettings?: any[], AuthenticatedSettings?: any[] } | null} connectorAuth
 * @param {{ authCount: number, extraPage1Content?: boolean }} ctx
 * @returns {{ hasPage1Content: boolean, hasAuthSettings: boolean, usePages: boolean }}
 */
function derivePages(connectorAuth, ctx) {
    const hasUserSettings = (connectorAuth?.UserSettings?.length ?? 0) > 0;
    const hasAuthSettings = (connectorAuth?.AuthenticatedSettings?.length ?? 0) > 0;
    const hasPage1Content = hasUserSettings || ctx.authCount > 1 || !!ctx.extraPage1Content;
    return { hasPage1Content, hasAuthSettings, usePages: hasPage1Content && hasAuthSettings };
}

/**
 * The connect primary-button label ladder, shared by the install/reconnect/child/user-connect
 * controllers. Mirrors the renderer's getConnectButtonLabel.
 * @param {{ isSubmitting: boolean, phase: string, hasAuthenticatedSettings: boolean }} connect
 * @param {{ HasRedirectUri?: boolean } | null | undefined} connectorAuth
 * @returns {string}
 */
function deriveConnectLabel(connect, connectorAuth) {
    if (connect.isSubmitting) return connectorAuth?.HasRedirectUri ? "Authenticating..." : "Connecting...";
    if (connect.phase === "initial" && (connect.hasAuthenticatedSettings || connectorAuth?.HasRedirectUri)) return "Authenticate";
    if (connect.phase === "connected") return "Reconnect";
    return "Connect";
}

// The full set of query params the OAuth round-trip writes into the address bar. Listed once so the
// readers (use-connect-flow.initFlow), the scrubbers (cleanAuthUrlParams), and the return-path builders
// (connect-user / popdock-view) cannot drift. Note buildReturnPath historically omitted connectorAuthId;
// stripping a superset is safe (a missing param is a no-op delete) and keeps the contract single-sourced.
// `flow` and `connectorSetupId` are dispatch params (which modal to reopen on return) — listed here so
// they're scrubbed once consumed, otherwise a manual page reload re-triggers the reopen off a stale URL.
// `multipleCredentials` and `name` are carried-value params the install/child round-trip reads back on
// return; listed so they're scrubbed once consumed (readers run before the scrub) and don't linger.
// `entitiesPending` is the install entity-selection return marker (written by the backend callback, read
// by the install controller); scrubbed once the controller parks on the Lists page so a reload can't
// re-trigger the Lists reopen.
const AUTH_URL_PARAMS = [
    "authSuccess", "authError", "authErrorDescription",
    "userValuesId", "validateSuccess", "connectorAuthId", "sessionToken",
    "flow", "connectorSetupId", "multipleCredentials", "name", "entitiesPending",
];

/**
 * Strip the auth round-trip params from a "path?query" string, preserving any unrelated query and the
 * pathname. Pure (takes a string, returns a string) so it is trivially testable without window.location.
 * @param {string} pathWithQuery
 * @returns {string}
 */
function stripAuthParams(pathWithQuery) {
    const [pathname, query = ""] = pathWithQuery.split("?");
    const params = new URLSearchParams(query);
    AUTH_URL_PARAMS.forEach(p => params.delete(p));
    const qs = params.toString();
    return qs ? `${pathname}?${qs}` : pathname;
}

export {
    deriveSubmitAction,
    derivePageStatuses,
    deriveInstallPageModel,
    deriveAuthenticatedSettingsVisibility,
    isConnectBusy,
    applyAuthorizeRedirect,
    deriveUnattendedAction,
    deriveHelpLink,
    derivePages,
    deriveConnectLabel,
    AUTH_URL_PARAMS,
    stripAuthParams,
};
